import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Academy.css";

import academyImg from "../../../../assets/images/home/academy/leftimg.png";
import tickIcon from "../../../../assets/icons/orangeTick.svg";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackAcademy = {
  title: "Academy",
  description:
    "The IRQGYP Academy provides training and certification for builders, contractors, and designers in Iraq to improve construction standards, focusing on gypsum systems as comprehensive solutions through specialized workshops and practical demonstrations.",
  item1: "EN / ASTM Certified",
  item2: "Quality-First Approach",
  item3: "Partnership System",
  item4: "Proudly Iraqi Expertise",
  item5: "Training Academy",
  buttonText: "Discover More",
  buttonLink: "/academy",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceAcademyPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return tryParseJsonObject(raw) || {};
  }
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const titleFromPayload = (c) => {
  const joined = [c?.titleLine1, c?.titleLine2].filter((x) => typeof x === "string" && x.trim()).join(" ").trim();
  return pickString(c?.title, c?.heading, c?.headline, joined);
};

const descriptionFromPayload = (c) =>
  pickString(c?.description, c?.body, c?.text, c?.summary, c?.content, c?.copy);

const getImageCandidates = (c) => {
  if (!c || typeof c !== "object") return [];
  return [
    c.image,
    c.leftImage,
    c.rightImage,
    c.heroImage,
    c.mediaUrl,
    c.MediaUrl,
    c.photo,
  ].filter((x) => x != null && x !== "");
};

const resolveFirstImage = (candidates) => {
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const itemKeyRegex = /^item(\d+)$/i;

const collectFeatureStrings = (payload) => {
  const pairs = [];
  Object.entries(payload || {}).forEach(([key, value]) => {
    const m = key.match(itemKeyRegex);
    if (!m) return;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n < 1) return;
    const text = typeof value === "string" ? value.trim() : "";
    if (text) pairs.push([n, text]);
  });
  pairs.sort((a, b) => a[0] - b[0]);
  return pairs.map(([, text]) => text);
};

const splitFeatures = (items) => {
  if (!items.length) return [[], []];
  const mid = Math.ceil(items.length / 2);
  return [items.slice(0, mid), items.slice(mid)];
};

const hasMeaningfulAcademyPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (titleFromPayload(c)) return true;
  if (descriptionFromPayload(c)) return true;
  if (pickString(c.buttonText, c.buttonLink, c.ctaText, c.ctaLink)) return true;
  if (collectFeatureStrings(c).length) return true;
  return Boolean(resolveFirstImage(getImageCandidates(c)));
};

const Academy = ({ content }) => {
  const navigate = useNavigate();

  const { title, description, featuresLeft, featuresRight, buttonText, buttonLink, imageSrc } = useMemo(() => {
    const payload = coalesceAcademyPayload(content);
    if (!hasMeaningfulAcademyPayload(payload) || !payload) {
      const fb = fallbackAcademy;
      const all = [fb.item1, fb.item2, fb.item3, fb.item4, fb.item5].filter(Boolean);
      const [left, right] = splitFeatures(all);
      return {
        title: fb.title,
        description: fb.description,
        featuresLeft: left,
        featuresRight: right,
        buttonText: fb.buttonText,
        buttonLink: fb.buttonLink,
        imageSrc: academyImg,
      };
    }

    const fromItems = collectFeatureStrings(payload);
    const fromAliases = [
      pickString(payload.feature1, payload.bullet1),
      pickString(payload.feature2, payload.bullet2),
      pickString(payload.feature3, payload.bullet3),
      pickString(payload.feature4, payload.bullet4),
      pickString(payload.feature5, payload.bullet5),
    ].filter(Boolean);

    const list =
      fromItems.length > 0
        ? fromItems
        : fromAliases.length > 0
          ? fromAliases
          : [
              fallbackAcademy.item1,
              fallbackAcademy.item2,
              fallbackAcademy.item3,
              fallbackAcademy.item4,
              fallbackAcademy.item5,
            ];

    const [featuresLeft, featuresRight] = splitFeatures(list);
    const resolvedImg = resolveFirstImage(getImageCandidates(payload));

    return {
      title: titleFromPayload(payload) || fallbackAcademy.title,
      description: descriptionFromPayload(payload) || fallbackAcademy.description,
      featuresLeft,
      featuresRight,
      buttonText: pickString(payload.buttonText, payload.ctaText, payload.buttonLabel) || fallbackAcademy.buttonText,
      buttonLink: pickString(payload.buttonLink, payload.ctaLink, payload.link) || fallbackAcademy.buttonLink,
      imageSrc: resolvedImg || academyImg,
    };
  }, [content]);

  return (
    <section className="academy">
      <div className="academy-container">
        <div className="academy-image">
          <img
            src={imageSrc}
            alt=""
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== academyImg) {
                e.currentTarget.src = academyImg;
              }
            }}
          />
        </div>

        <div className="academy-content">
          <h2>{title}</h2>

          <p>{description}</p>

          <div className="academy-features">
            <div className="features-col">
              {featuresLeft.map((item, index) => (
                <div key={`l-${index}`} className="feature-item">
                  <img src={tickIcon} alt="" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="features-col">
              {featuresRight.map((item, index) => (
                <div key={`r-${index}`} className="feature-item">
                  <img src={tickIcon} alt="" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="academy-btn" type="button" onClick={() => navigate(buttonLink)}>
            {buttonText}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Academy;
